import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Formik, Form } from 'formik';
import classNames from 'classnames';
import NewDesign from 'components/NewDesign';
import { reorder } from 'helpers/dnd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import CheckBoxField from 'customField/CheckBoxField';
import { AlignJustifyIcon } from 'lucide-react';
import { cn } from 'helpers/css';
import { get } from 'lodash';

/**
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.toggle
 * @param {string} props.className
 * @param {{ label: string, value: string, isHide: boolean, disableDrag: boolean }[]} props.columns
 * @param {(values: { label: string, value: string, isHide: boolean, disableDrag: boolean }[], formikHelpers) => void} props.onSubmit
 * @returns
 */
const DialogConfigOrderingColumns = ({ isOpen, toggle, className, columns, onSubmit }) => {
  return (
    <Formik
      // validationSchema={{}}
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={{
        columns: columns,
      }}
      onSubmit={onSubmit}
    >
      {(propsFormik) => {
        return (
          <Modal
            isOpen={isOpen}
            toggle={toggle}
            modalClassName='!tw-overflow-hidden'
            className={classNames('component:DialogConfigOrderingColumns', className)}
            backdrop={propsFormik.isSubmitting ? 'static' : true}
          >
            <Form>
              <ModalHeader toggle={toggle}>Ordering columns</ModalHeader>

              <ModalBody>
                <div className=''>
                  <DragDropContext
                    onDragEnd={(result) => {
                      if (!result.destination) {
                        return;
                      }

                      const destination = get(
                        propsFormik?.values?.columns,
                        result.destination.index
                      );

                      if (destination?.disableDrag) {
                        return;
                      }

                      const itemsOrdered = reorder(
                        propsFormik?.values?.columns,
                        result.source.index,
                        result.destination.index
                      );

                      propsFormik.setFieldValue('columns', itemsOrdered);
                    }}
                  >
                    <Droppable droppableId={`columns-layout`} type={`droppableContent`}>
                      {(provided, snapshot) => {
                        return (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className='d-flex flex-column gap-4px tw-max-h-[calc(100vh-200px)] tw-overflow-auto'
                          >
                            {(propsFormik?.values?.columns || []).map((c, idxContainer) => {
                              return (
                                <Draggable
                                  key={c.value}
                                  draggableId={c.value}
                                  isDragDisabled={c.disableDrag}
                                  disableInteractiveElementBlocking={c.disableDrag}
                                  index={idxContainer}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className='default-border tw-flex tw-p-2 tw-justify-between tw-bg-white dark:tw-bg-[var(--bg-dark)]'
                                    >
                                      <div>
                                        <NewDesign.Typography
                                          {...provided.dragHandleProps}
                                          className={cn(c.disableDrag && 'tw-invisible')}
                                        >
                                          <AlignJustifyIcon className='tw-icon tw-mr-3' />
                                        </NewDesign.Typography>

                                        <NewDesign.Typography>{c.label}</NewDesign.Typography>
                                      </div>

                                      <CheckBoxField
                                        disabled={c.disableDrag}
                                        checked={!c.isHide}
                                        onChangeCustomize={() =>
                                          propsFormik.setFieldValue(
                                            `columns.${idxContainer}.isHide`,
                                            !c.isHide
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        );
                      }}
                    </Droppable>
                  </DragDropContext>
                </div>
              </ModalBody>

              <ModalFooter>
                <NewDesign.Button
                  type='submit'
                  disabled={!propsFormik.dirty}
                  isLoading={propsFormik.isSubmitting}
                >
                  Save
                </NewDesign.Button>
                <NewDesign.Button variant='text' type='button' className='btn' onClick={toggle}>
                  Close
                </NewDesign.Button>
              </ModalFooter>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default DialogConfigOrderingColumns;
